import { Pipe, PipeTransform } from '@angular/core';
import * as moment from 'moment-timezone';

/**
 * How long ago, in the compact form a notification list wants: `3 hours`, `21 days`, `a month`.
 *
 * `fromNow(true)` rather than `fromNow()` — the suffix is dropped deliberately. In a column of
 * timestamps the word "ago" is on every row, so it carries no information and only makes the
 * column wider.
 *
 * Pure, so it does not recompute while the panel sits open. That is a real limitation and it is
 * accepted rather than papered over with a timer: the rows this renders are minutes to months old,
 * and none of those labels change within the seconds a dropdown is on screen. A row written while
 * the panel is open arrives through the live Firestore listener as a NEW object, so it gets its own
 * first render — it is only the ticking of an already-rendered label that is given up. If this ever
 * moves somewhere long-lived (a full notifications page left open on a second monitor), pass a
 * changing `now` as the second argument and the pipe will re-evaluate; there is nothing else to fix.
 */
@Pipe({
  name: 'timeAgo',
})
export class TimeAgoPipe implements PipeTransform {
  /**
   * `now` is never read. It exists so a caller that DOES need the label to tick can force this pipe
   * to recompute by passing a value that changes — Angular re-runs a pure pipe when any argument
   * changes reference, and the `date` on a stored row never does.
   */
  transform(value: Date | null | undefined, _now?: unknown): string {
    // A just-written row has a pending server timestamp and no date yet. Empty, not "Invalid date",
    // and not "just now" either — we do not know when it happened, and guessing would be a
    // plausible-looking lie for the one second before the real value arrives.
    if (!value) {
      return '';
    }

    const date = moment(value);

    return date.isValid() ? date.fromNow(true) : '';
  }
}
